import React, { useEffect, useMemo } from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom-v5-compat';

import Global from '../../../store/global-selectors';
import { AddShipFromButton } from './add-ship-from-button';
import commonStyles from '../settings-page.module.less';
import DeleteShipFromModalContextProvider from './contexts/delete-ship-from-modal-context-provider';
import EditShipFromModalContextProvider from './contexts/edit-ship-from-modal-context-provider';
import ShipFromPageErrorContextProvider from './contexts/ship-from-page-error-context-provider';
import messages from './messages';
import ShipFromPageErrorBanner from './ship-from-page-error-banner';
import ShipFromRow from './ship-from-row/ship-from-row';
import style from './style.module.less';
import { useShipFroms } from '../../../hooks/ship-froms/use-ship-froms';
import { useAreLocalDeliveryServicesAvailableForSeller } from '../../../hooks/use-are-local-delivery-services-available-for-seller';
import { SettingsPageTitle } from '../components/settings-page-title/settings-page-title';

const SHIPPY_SOURCE = 'shippy';

export const ShipFromLocations = () => {
  const shipFroms = useShipFroms();
  const location = useLocation();
  const navigate = useNavigate();
  const pendingPrefill = useSelector(Global.selectShippyPendingPrefill);

  const { shouldAutoOpenCreate, autoEditShipFromId } = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const source = searchParams.get('source');
    const isShippySource = source === SHIPPY_SOURCE;

    return {
      shouldAutoOpenCreate: isShippySource && searchParams.get('openCreateShipFrom') === 'true',
      autoEditShipFromId: isShippySource ? searchParams.get('editShipFromId') : null,
    };
  }, [location.search]);

  const prefillAddress = pendingPrefill?.target === 'ship-from' ? pendingPrefill.data : null;

  useEffect(() => {
    if (!shouldAutoOpenCreate && !autoEditShipFromId) return;
    navigate(location.pathname, { replace: true });
  }, [shouldAutoOpenCreate, autoEditShipFromId, navigate, location.pathname]);

  const hasLocalDeliveryEnabled = useAreLocalDeliveryServicesAvailableForSeller();

  const sortedShipFroms = useMemo(() => {
    const shipFromsCopy = [...shipFroms];
    shipFromsCopy.sort((shipFromA, shipFromB) => {
      const shipFromAName = shipFromA.name.toUpperCase();
      const shipFromBName = shipFromB.name.toUpperCase();
      if (shipFromAName < shipFromBName) return -1;
      if (shipFromBName < shipFromAName) return 1;
      return 0;
    });
    return shipFromsCopy;
  }, [shipFroms]);

  return (
    <div className={commonStyles['content-wrapper']}>
      <div className={style['settings-section']}>
        <SettingsPageTitle value={messages.header} />
        <div className={style.subtext}>
          <FormattedMessage {...messages.shipFromDescription} values={{ b: (chunks) => <b>{chunks}</b> }} />
        </div>
        <ShipFromPageErrorContextProvider>
          <div className={style['button-wrapper']}>
            <AddShipFromButton prefillAddress={prefillAddress} shouldAutoOpen={shouldAutoOpenCreate} />
          </div>
          <ShipFromPageErrorBanner />
          <div
            className={classnames(style['grid-wrapper'], {
              [style['grid-with-schedule']]: hasLocalDeliveryEnabled,
            })}
          >
            <div className={style['left-head-cell']}>
              <FormattedMessage {...messages.shipFromName} />
            </div>
            <div className={style['head-cell']}>
              <FormattedMessage {...messages.shipFromInventorySource} />
            </div>
            <div className={style['head-cell']}>
              <FormattedMessage {...messages.shipFromShipFromAddress} />
            </div>
            <div className={style['head-cell']}>
              <FormattedMessage {...messages.shipFromReturnToAddress} />
            </div>
            {hasLocalDeliveryEnabled ? (
              <div className={style['head-cell']}>
                <FormattedMessage {...messages.localDeliveryScheduleColumnHeader} />
              </div>
            ) : null}
            <div className={style['right-head-cell']}>
              <FormattedMessage {...messages.shipFromActions} />
            </div>
            {!sortedShipFroms?.length && (
              <>
                <div className={classnames(style['empty-state-cell'])}>
                  <FormattedMessage {...messages.noShipFroms} />
                </div>
              </>
            )}
            <DeleteShipFromModalContextProvider>
              <EditShipFromModalContextProvider autoEditShipFromId={autoEditShipFromId} prefillAddress={prefillAddress}>
                {sortedShipFroms?.map((shipFrom) => (
                  <ShipFromRow key={shipFrom.shipFromId} shipFrom={shipFrom} />
                ))}
              </EditShipFromModalContextProvider>
            </DeleteShipFromModalContextProvider>
          </div>
        </ShipFromPageErrorContextProvider>
      </div>
    </div>
  );
};
